import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Power, PowerOff } from 'lucide-react';

const AppManagement = () => {
  const { token } = useContext(AuthContext);
  const [apps, setApps] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_es: '',
    description: '',
    description_es: '',
    status: 'active',
    header_color: '#A8D5BA'
  });

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApps(response.data);
    } catch (error) {
      toast.error('Failed to fetch apps');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingApp) {
        await axios.put(`${API}/apps/${editingApp.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('App updated!');
      } else {
        await axios.post(`${API}/apps`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('App created!');
      }
      fetchApps();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const toggleAppStatus = async (app) => {
    const newStatus = app.status === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(`${API}/apps/${app.id}`, { ...app, status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`App ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      fetchApps();
    } catch (error) {
      toast.error('Failed to update app status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this app?')) return;
    try {
      await axios.delete(`${API}/apps/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('App deleted');
      fetchApps();
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  const openEditDialog = (app) => {
    setEditingApp(app);
    setFormData({
      name: app.name,
      name_es: app.name_es,
      description: app.description,
      description_es: app.description_es,
      status: app.status,
      header_color: app.header_color
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_es: '',
      description: '',
      description_es: '',
      status: 'active',
      header_color: '#A8D5BA'
    });
    setEditingApp(null);
  };

  const pastelColors = [
    '#A8D5BA', // Mint
    '#FFB3BA', // Pink
    '#FFDFBA', // Peach
    '#FFFFBA', // Yellow
    '#BAE1FF', // Blue
    '#E7C6FF', // Lavender
  ];

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Application Management</CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-app-btn" style={{ backgroundColor: '#6B9F3E' }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add App
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingApp ? 'Edit App' : 'Create New App'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name (English)</Label>
                      <Input
                        id="name"
                        data-testid="app-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name_es">Nombre (Español)</Label>
                      <Input
                        id="name_es"
                        data-testid="app-name-es-input"
                        value={formData.name_es}
                        onChange={(e) => setFormData({ ...formData, name_es: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (English)</Label>
                    <Input
                      id="description"
                      data-testid="app-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description_es">Descripción (Español)</Label>
                    <Input
                      id="description_es"
                      data-testid="app-description-es-input"
                      value={formData.description_es}
                      onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Header Color (Pastel)</Label>
                    <div className="flex gap-2 flex-wrap">
                      {pastelColors.map(color => (
                        <button
                          key={color}
                          type="button"
                          className="w-10 h-10 rounded-lg border-2 hover:scale-110 transition-transform"
                          style={{ 
                            backgroundColor: color,
                            borderColor: formData.header_color === color ? '#000' : '#ddd'
                          }}
                          onClick={() => setFormData({ ...formData, header_color: color })}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      data-testid="app-status-select"
                      className="w-full border rounded-md px-3 py-2"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </div>
                  
                  <Button type="submit" className="w-full" data-testid="app-submit-btn" style={{ backgroundColor: '#6B9F3E' }}>
                    {editingApp ? 'Update App' : 'Create App'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {apps.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No apps yet. Create your first app to get started.</p>
            ) : (
              apps.map((app) => (
                <div 
                  key={app.id} 
                  className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  data-testid="app-item"
                >
                  <div className="p-3" style={{ backgroundColor: app.header_color }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-800">{app.name} / {app.name_es}</h3>
                        <p className="text-sm text-slate-600">{app.description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleAppStatus(app)}
                          className="p-2 rounded-full hover:bg-white/50 transition-colors"
                          data-testid="toggle-app-status-btn"
                        >
                          {app.status === 'active' ? (
                            <Power className="h-5 w-5 text-green-700" />
                          ) : (
                            <PowerOff className="h-5 w-5 text-red-700" />
                          )}
                        </button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(app)}
                          data-testid="edit-app-btn"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(app.id)}
                          data-testid="delete-app-btn"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-2 bg-white text-xs text-slate-500">
                    Status: <span className={app.status === 'active' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{app.status}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppManagement;